import React, { useState } from "react";
import JobCard from "../components/JobCard";
import jobs from "../data/jobs";
import { Search, MapPin, Filter } from "lucide-react";

const Home = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [showFilters, setShowFilters] = useState(false);

  const categories = ["All", ...new Set(jobs.map(job => job.category))];

  const filteredJobs = jobs.filter(job => {
    const matchesSearch = job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         job.company.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === "All" || job.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="pb-12">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-slate-900 pt-12 pb-16 lg:pt-24 lg:pb-32">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full opacity-10">
          <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-blue-500 rounded-full blur-[100px] animate-pulse"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-indigo-500 rounded-full blur-[100px] animate-pulse delay-700"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-10 lg:mb-12">
            <h1 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Find Your <span className="text-blue-500 font-extrabold">Dream Career</span> Today
            </h1>
            <p className="text-slate-400 text-base sm:text-lg lg:text-xl font-medium">
              Connect with top companies and explore thousands of job opportunities across the globe.
            </p>
          </div>

          {/* Search Bar - Improved Contrast & Mobile Layout */}
          <div className="bg-white/95 backdrop-blur-md max-w-4xl mx-auto p-2 rounded-2xl flex flex-col md:flex-row gap-2 shadow-2xl">
            <div className="flex-1 flex items-center px-4 gap-3 border-b md:border-b-0 md:border-r border-slate-200">
              <Search className="text-slate-500 shrink-0" size={20} />
              <input
                type="text"
                placeholder="Job title or keywords..."
                className="w-full py-3 md:py-4 bg-transparent text-slate-800 font-semibold outline-none placeholder:text-slate-400 text-sm md:text-base"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="flex-1 hidden md:flex items-center px-4 gap-3">
              <MapPin className="text-slate-500" size={20} />
              <input
                type="text"
                placeholder="City or remote"
                className="w-full py-4 bg-transparent text-slate-800 font-semibold outline-none placeholder:text-slate-400"
              />
            </div>
            <button className="bg-blue-600 hover:bg-blue-700 text-white px-10 py-4 rounded-xl font-bold transition-all active:scale-95 shadow-lg shadow-blue-500/30">
              Search
            </button>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 mt-12">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-4">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Recommended Jobs</h2>
            <p className="text-slate-500">Based on your preferences</p>
          </div>
          <div className="flex items-center gap-3 overflow-x-auto w-full md:w-auto pb-2 md:pb-0">
            <button 
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 border rounded-xl text-sm font-medium transition-colors ${showFilters ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
            >
              <Filter size={18} />
              Filters
            </button>
            {showFilters && (
              <div className="flex gap-2">
                {categories.map(cat => (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    className={`px-4 py-2 rounded-xl text-sm font-bold transition-all ${selectedCategory === cat ? 'bg-blue-600 text-white shadow-md' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {filteredJobs.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-3xl shadow-sm border border-slate-100">
            <div className="bg-slate-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="text-slate-300" size={32} />
            </div>
            <h3 className="text-xl font-bold text-slate-800">No results found</h3>
            <p className="text-slate-500">Try adjusting your search or category filters.</p>
            {(searchTerm || selectedCategory !== "All") && (
              <button 
                onClick={() => {setSearchTerm(""); setSelectedCategory("All")}}
                className="mt-6 text-blue-600 font-bold hover:underline"
              >
                Clear all filters
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredJobs.map((job) => (
              <JobCard key={job.id} job={job} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Home;

